import { Select } from 'antd'
import { useEffect, useState } from 'react'
import { apiGetOutletGeoOptions } from '../../api'

// Module-level cache — geo options rarely change; refetch on full page reload.
let geoCache: { provinces: string[]; districtsByProvince: Record<string, string[]> } | null = null
let geoFetchPromise: Promise<void> | null = null

async function ensureGeoLoaded() {
    if (geoCache) return
    if (!geoFetchPromise) {
        geoFetchPromise = (async () => {
            try {
                const resp: any = await apiGetOutletGeoOptions({})
                geoCache = {
                    provinces: resp?.data?.provinces || [],
                    districtsByProvince: resp?.data?.districtsByProvince || {},
                }
            } catch {
                geoCache = { provinces: [], districtsByProvince: {} }
            } finally {
                geoFetchPromise = null
            }
        })()
    }
    await geoFetchPromise
}

interface Props {
    provinces?: string[]
    districts?: string[]
    onProvincesChange?: (values: string[]) => void
    onDistrictsChange?: (values: string[]) => void
    showDistrict?: boolean
    style?: React.CSSProperties
}

export default function ProvinceDistrictSelect({
    provinces = [],
    districts = [],
    onProvincesChange,
    onDistrictsChange,
    showDistrict = true,
    style,
}: Props) {
    const [provinceOptions, setProvinceOptions] = useState<{ label: string; value: string }[]>([])
    const [districtOptions, setDistrictOptions] = useState<{ label: string; value: string }[]>([])

    useEffect(() => {
        ensureGeoLoaded().then(() => {
            if (geoCache) {
                setProvinceOptions(geoCache.provinces.map(p => ({ label: p, value: p })))
            }
        })
    }, [])

    useEffect(() => {
        if (!geoCache) return
        if (provinces.length === 0) {
            const all: Record<string, true> = {}
            Object.values(geoCache.districtsByProvince).forEach(list => list.forEach(d => { all[d] = true }))
            setDistrictOptions(Object.keys(all).sort().map(d => ({ label: d, value: d })))
        } else {
            const merged: Record<string, true> = {}
            provinces.forEach(p => (geoCache!.districtsByProvince[p] || []).forEach(d => { merged[d] = true }))
            setDistrictOptions(Object.keys(merged).sort().map(d => ({ label: d, value: d })))
        }
    }, [provinces.join('|')])

    return (
        <div style={{ display: 'flex', gap: 8, ...style }}>
            <Select
                mode='multiple'
                allowClear
                placeholder='Tỉnh/Thành'
                value={provinces}
                options={provinceOptions}
                onChange={onProvincesChange}
                style={{ minWidth: 200 }}
                maxTagCount='responsive'
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
            {showDistrict && (
                <Select
                    mode='multiple'
                    allowClear
                    placeholder='Quận/Huyện'
                    value={districts}
                    options={districtOptions}
                    onChange={onDistrictsChange}
                    style={{ minWidth: 200 }}
                    maxTagCount='responsive'
                    showSearch
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
            )}
        </div>
    )
}
